import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Edit, 
  Trash2, 
  User, 
  Mail,
  Calendar,
  Shield,
  Eye,
  EyeOff,
  AlertCircle
} from 'lucide-react';
import { AdminUser, CreateAdminUserRequest } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: ${props => 
    props.variant === 'primary' ? 'none' : 
    props.variant === 'danger' ? '1px solid #f87171' : '1px solid #d1d5db'
  };
  background: ${props => 
    props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 
    props.variant === 'danger' ? '#fef2f2' : 'white'
  };
  color: ${props => 
    props.variant === 'primary' ? 'white' : 
    props.variant === 'danger' ? '#dc2626' : '#374151'
  };
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    ${props => {
      if (props.variant === 'primary') {
        return 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);';
      } else if (props.variant === 'danger') {
        return 'background: #fee2e2; border-color: #fca5a5;';
      } else {
        return 'background: #f3f4f6; border-color: #9ca3af;';
      }
    }}
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  gap: 12px;
  
  .icon {
    color: #3b82f6;
    flex-shrink: 0;
  }
  
  .content {
    color: #1e40af;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const UserCard = styled.div<{ isCurrentUser?: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.isCurrentUser ? '#ed2024' : '#e5e7eb'};
  transition: all 0.2s;
  ${props => props.isCurrentUser && 'background: #f9fafb;'}
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
    border-color: ${props => props.isCurrentUser ? '#ed2024' : '#d1d5db'};
  }
  
  .user-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    
    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .user-avatar {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: 1rem;
      }
      
      .user-details {
        .user-email {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
          
          .current-badge {
            background: #ed2024;
            color: white;
            font-size: 0.75rem;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: 500;
          }
        }
        
        .user-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          color: #6b7280;
          font-size: 0.875rem;
          
          .meta-item {
            display: flex;
            align-items: center;
            gap: 4px;
          }
        }
      }
    }
    
    .user-actions {
      display: flex;
      gap: 8px;
    }
  }
`;

const StatusBadge = styled.span<{ status: 'active' | 'inactive' }>`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.status === 'active' ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.status === 'active' ? '#166534' : '#991b1b'};
`;

const RoleBadge = styled.span<{ role: 'super_admin' | 'admin' }>`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.role === 'super_admin' ? '#fef3c7' : '#e0e7ff'};
  color: ${props => props.role === 'super_admin' ? '#92400e' : '#3730a3'};
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  
  h2 {
    margin: 0 0 20px 0;
    color: #1f2937;
    font-size: 1.5rem;
    font-weight: 600;
  }
`;

const Form = styled.form`
  .form-group {
    margin-bottom: 16px;
    
    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      color: #374151;
    }
    
    input, select {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 0.875rem;
      
      &:focus {
        outline: none;
        border-color: #ed2024;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      }
    }
    
    .password-input {
      position: relative;
      
      input {
        padding-right: 40px;
      }
      
      .password-toggle {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: #6b7280;
        cursor: pointer;
        padding: 4px;
        
        &:hover {
          color: #374151;
        }
        
        svg {
          width: 16px;
          height: 16px;
        }
      }
    }
    
    .help-text {
      font-size: 0.75rem;
      color: #6b7280;
      margin-top: 4px;
    }
  }
  
  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  
  .empty-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 16px;
    color: #d1d5db;
  }
  
  h3 {
    margin: 0 0 8px 0;
    color: #374151;
    font-size: 1.125rem;
    font-weight: 600;
  }
  
  p {
    margin: 0 0 20px 0;
    font-size: 0.875rem;
  }
`;

const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'admin' as 'admin' | 'super_admin'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAdminUsers();

      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        toast.error('Failed to load admin users');
      }
    } catch (error) {
      console.error('Error loading admin users:', error);
      toast.error('Failed to load admin users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (user?: AdminUser) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        role: 'admin'
      });
    }
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setShowPassword(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData: any = {
          email: formData.email,
          role: formData.role
        };
        
        if (formData.password) {
          updateData.password = formData.password;
        }

        const response = await apiService.updateAdminUser(editingUser.id, updateData);
        if (response.success) {
          toast.success('Admin user updated successfully');
          handleCloseModal();
          loadUsers();
        } else {
          toast.error(response.error || 'Failed to update admin user');
        }
      } else {
        // Create new user
        const createData: CreateAdminUserRequest = {
          email: formData.email,
          password: formData.password,
          role: formData.role
        };

        const response = await apiService.createAdminUser(createData);
        if (response.success) {
          toast.success('Admin user created successfully');
          handleCloseModal();
          loadUsers();
        } else {
          toast.error(response.error || 'Failed to create admin user');
        }
      }
    } catch (error: any) {
      console.error('Error saving admin user:', error);
      const errorMessage = error?.response?.data?.error || 'Failed to save admin user';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (user: AdminUser) => {
    if (currentUser?.id === user.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (!window.confirm(`Delete admin user ${user.email}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiService.deleteAdminUser(user.id);
      if (response.success) {
        toast.success('Admin user deleted successfully');
        loadUsers();
      } else {
        toast.error(response.error || 'Failed to delete admin user');
      }
    } catch (error: any) {
      console.error('Error deleting admin user:', error);
      const errorMessage = error?.response?.data?.error || 'Failed to delete admin user';
      toast.error(errorMessage);
    }
  };

  const getUserInitials = (email: string) => {
    const name = email.split('@')[0];
    return name.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>Admin Users</h1>
        <ActionButton variant="primary" onClick={() => handleOpenModal()}>
          <Plus />
          Add Admin User
        </ActionButton>
      </PageHeader>

      <InfoBox>
        <AlertCircle className="icon" />
        <div className="content">
          <strong>Important:</strong> Admin users have full access to the system. You cannot modify or delete your own account.
          Super Admin users have elevated privileges for system-wide settings.
        </div>
      </InfoBox>

      {users.length === 0 ? (
        <EmptyState>
          <User className="empty-icon" />
          <h3>No admin users found</h3>
          <p>Create the first admin user to get started.</p>
          <ActionButton variant="primary" onClick={() => handleOpenModal()}>
            <Plus />
            Create First Admin User
          </ActionButton>
        </EmptyState>
      ) : (
        users.map((user) => (
          <UserCard key={user.id} isCurrentUser={currentUser?.id === user.id}>
            <div className="user-header">
              <div className="user-info">
                <div className="user-avatar">
                  {getUserInitials(user.email)}
                </div>
                <div className="user-details">
                  <div className="user-email">
                    {user.email}
                    {currentUser?.id === user.id && (
                      <span className="current-badge">You</span>
                    )}
                  </div>
                  <div className="user-meta">
                    <div className="meta-item">
                      <Shield />
                      <RoleBadge role={user.role}>
                        {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </RoleBadge>
                    </div>
                    <div className="meta-item">
                      <StatusBadge status={user.isActive ? 'active' : 'inactive'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </div>
                    {user.lastLoginAt && (
                      <div className="meta-item">
                        <Calendar />
                        Last login: {formatDate(user.lastLoginAt)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="user-actions">
                <ActionButton 
                  onClick={() => handleOpenModal(user)}
                  disabled={currentUser?.id === user.id}
                  title={currentUser?.id === user.id ? 'Cannot edit your own account' : 'Edit user'}
                >
                  <Edit />
                  Edit
                </ActionButton>
                <ActionButton 
                  variant="danger" 
                  onClick={() => handleDeleteUser(user)}
                  disabled={currentUser?.id === user.id}
                  title={currentUser?.id === user.id ? 'Cannot delete your own account' : 'Delete user'}
                >
                  <Trash2 />
                  Delete
                </ActionButton>
              </div>
            </div>
          </UserCard>
        ))
      )}

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <h2>{editingUser ? 'Edit Admin User' : 'Add New Admin User'}</h2>
          <Form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="admin@example.com"
              />
            </div>
            
            <div className="form-group">
              <label>Password {editingUser && '(leave blank to keep current)'}</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Leave blank to keep current password' : 'Enter password'}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
              {!editingUser && (
                <div className="help-text">
                  Password must be at least 8 characters long
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'super_admin' })}
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
              <div className="help-text">
                Super Admins have elevated privileges for system-wide settings
              </div>
            </div>

            <div className="form-actions">
              <ActionButton type="button" onClick={handleCloseModal}>
                Cancel
              </ActionButton>
              <ActionButton type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
              </ActionButton>
            </div>
          </Form>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default AdminUsers;

